import React, { useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Grid,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDispatch, useSelector } from "react-redux";
import { getEventInsightMetric } from "../../../store/metric/actions";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { NextPlanOutlined } from "@mui/icons-material";
import ApiLoading from "../components/ApiLoading";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";

// Native JS date formatting
function formatDateTime(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date)) return "-";
  return (
    date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
    " @ " +
    date.toLocaleTimeString(undefined, { hour12: false })
  );
}

const EventInsight = () => {
  const dispatch = useDispatch();
  const organizations = useOrganizations();
  const orgCode = organizations?.[0]?.orgcode;

  const { eventInsightMetric, eventInsightMetricLoading } = useSelector(
    (state) => ({
      eventInsightMetric: state.metricInfo.eventInsightMetric,
      eventInsightMetricLoading: state.metricInfo.eventInsightMetricLoading,
    })
  );

  useEffect(() => {
    if (orgCode) {
      dispatch(getEventInsightMetric(orgCode));
    }
  }, [dispatch, orgCode]);

  // Fallbacks for missing data
  const data = eventInsightMetric || {};
  const {
    DataFetchedLast,
    FailedConsoleLogins,
    UnauthorizedAPICalls,
    FailedLoginIPs,
    CreatedAssetChangesCount,
    DeletedAssetChangesCount,
  } = data;
  const fetchedAt = DataFetchedLast || null;
  const failedConsoleLogins = FailedConsoleLogins ?? 0;
  const unauthorizedAPICalls = UnauthorizedAPICalls ?? 0;
  const ipsWithFailedLogins = FailedLoginIPs ?? 0;
  const assetsCreated = CreatedAssetChangesCount ?? 0;
  const assetsDeleted = DeletedAssetChangesCount ?? 0;

  const handleGoTo = () => {
    window.location.href = "/cloudevent";
  };

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        padding: "8px 12px",
        height: { xs: 340, sm: 320 },
        display: "flex",
        flexDirection: "column",
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ mb: 1, paddingBottom: "2px", borderBottom: `1px solid` }}
      >
        <Box display="flex" alignItems="center" minWidth={0}>
          <CloudProviderMark />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Cloud Event
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Open Detailed View">
            <IconButton
              aria-label="go-to"
              sx={{ color: "text.secondary", fontSize: "20px", transform: "rotate(-45deg)" }}
              onClick={handleGoTo}
            >
              <NextPlanOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ alignSelf: "center", height: "18px" }} />
          <IconButton
            aria-label="settings"
            className="drag-handle"
            sx={{ color: "text.secondary", fontSize: "20px" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      {eventInsightMetricLoading ? (
        <ApiLoading variant="card" height={150} width="100%" />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%", mt: 1 }}>
          {/* Data Fetched On - highlighted */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            mb: 1.5,
            mt: 0.5,
            width: 'fit-content',
            mx: 'auto',
          }}>
            <Box sx={{ mr: 1, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8v5l3 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/></svg>
            </Box>
            <Typography variant="body2">
              Data Fetched On: {formatDateTime(fetchedAt)}
            </Typography>
          </Box>

          {/* Metrics - CSPM style */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Failed Console Logins */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">Failed Console Logins</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{failedConsoleLogins}</Typography>
            </Box>
            <Divider />
            {/* UnAuthorized API Calls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">Unauthorized API Calls</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{unauthorizedAPICalls}</Typography>
            </Box>
            <Divider />
            {/* IPs with Failed Logins */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">IPs with Failed Logins</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{ipsWithFailedLogins}</Typography>
            </Box>
            <Divider />
            {/* Assets Created */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">Assets Created</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{assetsCreated}</Typography>
            </Box>
            <Divider />
            {/* Assets Deleted */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Typography variant="body2">Assets Deleted</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{assetsDeleted}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EventInsight;
